<script lang="ts">
  import { onMount } from 'svelte';
  import { csv } from 'd3-fetch';
  import { scaleLinear, scaleLog, scaleSqrt, scaleOrdinal } from 'd3-scale';
  import { extent } from 'd3-array';
  import AxisLeft from '$lib/components/Basics/AxisLeftV5.svelte';
  import AxisBottom from '$lib/components/Basics/AxisBottomV5.svelte';
  import Labels from '$lib/components/Basics/Labels.svelte';
  import RadialLegend from '$lib/components/Basics/RadialLegend.svelte';
  import CategoryLegend from '$lib/components/Basics/CategoryLegend.svelte';
  import Quadtree from '$lib/components/Basics/Quadtree.svelte';

  // 1. Getting the data
  let data = $state(null);

  // We will use csv from d3 to fetch the data and we'll sort it by descending gdp
  // download data on: https://datavisualizationwithsvelte.com/data/world_bank.csv
  onMount(() => {
    csv('/data/world_bank.csv')
      .then((unsorted_data) => unsorted_data.sort((a, b) => b.gdp - a.gdp))
      .then((sorted_data) => (data = sorted_data));
  });

  // 2. Dimensions, Margins & Scales

  // We will be using scale functions from D3 to map our data points
  // within the dimensions of our svg.
  // Set the scale functions when the data is available.
  let width = $state(null);

  const height = 500;
  const margin = { top: 40, right: 200, bottom: 20, left: 35 };

  const continents = [
    'North America',
    'South America',
    'Asia',
    'Europe',
    'Africa',
    'Oceania'
  ];

  // The $derived rune in Svelte 5 creates a reactive value derived from other reactive values.
  const xScale = $derived(
    data && width
      ? scaleLog()
          .domain(extent(data, (d) => +d.gdp / +d.population))
          .range([margin.left, width - margin.right])
      : null
  );

  const yScale = $derived(
    data
      ? scaleLinear()
          .domain(extent(data, (d) => +d.life_expectancy))
          .range([height - margin.bottom, margin.top])
      : null
  );

  // We use the `scaleSqrt` function from D3 to make the circle's area proportional to the data values.
  // This approach better aligns with how we perceive size,
  // as we intuitively sense the area of a circle rather than its radius:

  const radiusScale = $derived(
    data
      ? scaleSqrt()
          .domain(extent(data, (d) => +d.population))
          .range([2, 20])
      : null
  );

  // We will use D3 scaleOrdinal for mapping a color to each continent.
  const colors = scaleOrdinal()
    .range([
      '#e0dff7',
      '#EFB605',
      '#FFF84A',
      '#FF0266',
      '#45FFC8',
      '#FFC9C9',
      '#A8a1ff',
      '#2172FF',
      '#45FFC8'
    ])
    .domain([
      'All',
      'America',
      'Asia',
      'Europe',
      'South America',
      'Oceania',
      'Africa'
    ]);
</script>

<div
  class="flex max-w-3xl flex-col items-center lg:flex-row"
  bind:clientWidth={width}>
  <div class="relative">
    {#if data && width && xScale && yScale && radiusScale}
      <Quadtree
        {data}
        {xScale}
        {yScale}
        {width}
        {height}
        {margin}
        searchRadius={30}>
        {#snippet children({ x, y, found, visible })}
          <div
            class="circle"
            style="top:{y}px;left:{x.circle}px;display: {visible
              ? 'block'
              : 'none'}; width: {radiusScale(+found.population) * 2 +
              5}px ; height: {radiusScale(+found.population) * 2 + 5}px;" />
          <div
            class="tooltip pointer-events-none bg-gray-50 bg-opacity-90 text-gray-900"
            style="top:{y + 5}px;left:{x.square + 10}px;display: {visible
              ? 'block'
              : 'none'};">
            <h1 class="mb-1 text-base text-gray-900">{found.country}</h1>
            GDP: {Number(found.gdp / 100000000).toFixed(1) + ' Bil. $'}<br />
            Pop.: {Number(+found.population / 1000000).toFixed(1) +
              ' Mil.'}<br />
            Life Expectancy: {Number(+found.life_expectancy).toFixed(1)}
          </div>
        {/snippet}
      </Quadtree>

      <svg {width} {height}>
        <g>
          <AxisLeft {yScale} {margin} {height} {width} />
          <AxisBottom {xScale} {margin} {height} {width} />

          <Labels
            labelforx={true}
            {width}
            {height}
            {margin}
            yoffset={-30}
            xoffset={-170}
            label={'GDP per capita →'} />
          <Labels
            labelfory={true}
            textanchor={'start'}
            {width}
            {height}
            {margin}
            yoffset={10}
            xoffset={10}
            label={'Life Expectancy ↑'} />
          {#each data as d, i}
            <circle
              class={d.continent.split(' ').join('')}
              cx={xScale(+d.gdp / +d.population)}
              cy={yScale(+d.life_expectancy)}
              r={radiusScale(+d.population)}
              fill={colors(d.continent)} />
          {/each}
        </g>
        <g transform="translate({width - margin.right},300)">
          <RadialLegend {width} {height} {margin} {radiusScale} />
        </g>
        <g transform="translate({width - margin.right},130)">
          <CategoryLegend
            legend_data={continents}
            legend_color_function={colors}
            space={80} />
        </g>
      </svg>
    {/if}
  </div>
</div>

<style>
  .tooltip {
    position: absolute;
    font-family: 'Poppins', sans-serif !important;
    min-width: 8em;
    line-height: 1.2;
    font-size: 0.875rem;
    z-index: 1;
    padding: 6px;
    transition:
      left 100ms ease,
      top 100ms ease;
  }

  .circle {
    position: absolute;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    width: 10px;
    height: 10px;
    border: 1px solid #fff;
    transition:
      left 300ms ease,
      top 300ms ease;
  }
</style>
